import React from "react";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import useStyles from "@hooks/useStyles";
import Header from "@components/Main/Shared/Header";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Footer from "@components/Main/Shared/Footer";
import useConstants from "@hooks/useConstants";
import Typography from "@material-ui/core/Typography";
import { adjective as proAdjective } from "@language/provider";
import { adjective as clientAdjective } from "@language/client";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";

const { BRAND_NAME } = process.env;

const Reviews = () => {
	const classes = useStyles("Terms");

	const { backgroundImage } = useConstants("BackgroundViewModel");

	const { sections, elfsight } = useConstants("ReviewsFAQViewModel");

	const { mobile, desktop } = backgroundImage;

	return (
		<React.Fragment>
			<Header />

			<section className={classes.hero}>
				<picture className={classes.picture}>
					<source
						srcSet={`${mobile} 300w, ${mobile} 768w, ${desktop} 1280w, ${desktop} 3200w`}
						sizes="100vw"
					/>
					<img
						sizes="100vw"
						srcSet={`${mobile} 300w, ${mobile} 768w, ${desktop} 1280w, ${desktop} 3200w`}
						src={desktop}
						className={classes.backgroundImage}
						fetchpriority="high"
					/>
				</picture>

				<Box
					height="100%"
					display={"flex"}
					alignItems={"center"}
					flexDirection={"column"}
					justifyContent={"center"}
					position="relative"
					color="#FFF"
					pt={6}
					pb={6}
					pr={4}
					pl={4}
				>
					<Box
						component={"h1"}
						fontWeight={700}
						lineHeight={1.4}
						fontSize={{ xs: 28, sm: 40 }}
						textAlign={"center"}
						mt={0}
						mb={2}
					>
						See what people say about us.
					</Box>
					<div>
						Read reviews from {clientAdjective.plural} and {proAdjective.plural}{" "}
						who use {BRAND_NAME}.
					</div>
				</Box>
			</section>

			<Box p={"36px 0px"} bgcolor={"#f8f9fa"}>
				<Container maxWidth={"md"}>
					<Box
						p={{ xs: "24px", sm: "40px 80px 60px" }}
						style={{ boxShadow: "rgba(60, 64, 67, 0.15) 0px 1px 3px 1px" }}
						bgcolor={"#FFF"}
					>
						<Box lineHeight={1.6}>
							<Box
								component={"h2"}
								fontWeight={700}
								lineHeight={1.4}
								fontSize={{ xs: 22, sm: 32 }}
								textAlign={"center"}
								mt={0}
								mb={6}
							>
								{BRAND_NAME} reviews
							</Box>

							<script src={"https://apps.elfsight.com/p/platform.js"} defer />
							<div className={elfsight} />
						</Box>
					</Box>
				</Container>
			</Box>

			<Box bgcolor={"#c4f0ff"}>
				<Box p={"40px 16px 80px"}>
					<Container maxWidth={"md"}>
						<Box
							component={"h1"}
							fontSize={{ xs: 32, sm: 38 }}
							lineHeight={1.3}
							fontWeight={700}
							mb={16}
							textAlign={"center"}
						>
							{clientAdjective.plural.ucFirst()} are looking for{" "}
							{proAdjective.plural} like you.
						</Box>
						<Grid container spacing={8}>
							<Grid item xs={12} sm={4}>
								<Box
									display={"flex"}
									alignItems={"center"}
									justifyContent={"center"}
									flexDirection={"column"}
								>
									<Box fontSize={32} mb={4}>
										500K
									</Box>
									<Box textAlign={"center"}>
										{clientAdjective.plural} helped
									</Box>
								</Box>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Box
									display={"flex"}
									alignItems={"center"}
									justifyContent={"center"}
									flexDirection={"column"}
								>
									<Box fontSize={32} mb={4}>
										10,000+
									</Box>
									<Box textAlign={"center"}>
										{clientAdjective.plural} find tutors every month
									</Box>
								</Box>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Box
									display={"flex"}
									alignItems={"center"}
									justifyContent={"center"}
									flexDirection={"column"}
								>
									<Box fontSize={32} mb={4}>
										100+
									</Box>
									<Box textAlign={"center"}>services offered</Box>
								</Box>
							</Grid>
						</Grid>
					</Container>
				</Box>
			</Box>

			<Box p={"36px 0px"} bgcolor={"#f8f9fa"}>
				<Container maxWidth={"md"}>
					<Box
						p={{ xs: "24px", sm: "40px 80px 60px" }}
						style={{ boxShadow: "rgba(60, 64, 67, 0.15) 0px 1px 3px 1px" }}
						bgcolor={"#FFF"}
					>
						<Box lineHeight={1.6}>
							<Box
								component={"h2"}
								fontWeight={700}
								lineHeight={1.4}
								fontSize={{ xs: 22, sm: 32 }}
								textAlign={"center"}
								mt={0}
								mb={6}
							>
								Frequently asked questions
							</Box>

							<Grid container spacing={2}>
								{sections.map((section, index) => (
									<React.Fragment key={`FAQ-${index}`}>
										<Grid item xs={12}>
											<div>
												<Box
													component={"h3"}
													fontSize={20}
													fontWeight={700}
													lineHeight={1.5}
													mt={3}
													mb={4}
												>
													{section.question}
												</Box>
												<Box fontSize={16} lineHeight={1.6} color={"grey.800"}>
													<div
														dangerouslySetInnerHTML={{ __html: section.answer }}
													/>
												</Box>
											</div>
										</Grid>
									</React.Fragment>
								))}
							</Grid>
						</Box>
					</Box>
				</Container>
			</Box>

			<Box
				component="section"
				className={classes.wrapper}
				display={"flex"}
				alignItems={"center"}
				justifyContent={"center"}
			>
				<Container maxWidth="md">
					<Typography component="h2" className={classes.mainH2}>
						{`Are you a five-star ${proAdjective.singular}?`}
					</Typography>

					<Typography component="h3" className={classes.mainH3}>
						{`Create a profile to reach millions of ${clientAdjective.plural}.`}
					</Typography>

					<ul className={classes.ul}>
						<li>Attract and engage new customers.</li>
						<li>Build and strengthen your online reputation.</li>
						<li>Get the tools you need to run a successful business.</li>
					</ul>

					<Box component={"div"} mt={6}>
						<Button
							href="/pro"
							variant="contained"
							size={"large"}
							color="secondary"
						>
							{`Create a profile`}
						</Button>
					</Box>
				</Container>
			</Box>

			<Footer />
		</React.Fragment>
	);
};

export default Reviews;
export const loader = (route) =>
	loadable(() => import("./Reviews.component"), { ssr: !!route.ssr });
